import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["scope", "id", "title", "resultMapper"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { PLACEHOLDER_EXPERIENCE } from '../aggregator-client-context';
import { useSearchSessionId } from '../../common/search-session-provider';
import { useTypedAggregatorClient } from '../aggregator-client-context/aggregator-client-context';
import { SITE_FILTER_TYPE_NAME } from '../filters';
var MAX_DEFAULT_SITES = 3;

/**
 * A definition of a {@link Section} in the Search Platform which is backed by
 * xpsearch-aggregator. Enforces the section to have a scope and a more
 * strongly typed resultMapper for transforming results for the specified scope
 * into a common format.
 */

export var createSearchSupplier = function createSearchSupplier(aggregatorClient, sections, context, experience) {
  return /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
      var query, allFilters, workspaces, allPermissionedScopes, defaultSites, siteFilter, sites, filters, permittedSections, scopes, _yield$aggregatorClie, response, sectionsWithResults, scopesWithSize, reponseHasSize, totalSizeAsReportedByAPI;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            query = _ref.query, allFilters = _ref.filters, workspaces = _ref.workspaces, allPermissionedScopes = _ref.sectionIds;
            defaultSites = workspaces === null || workspaces === void 0 ? void 0 : workspaces.slice(0, MAX_DEFAULT_SITES).map(function (workspace) {
              return workspace.id;
            }); // Extract sites from filters
            siteFilter = allFilters.find(function (filter) {
              return filter['@type'] === SITE_FILTER_TYPE_NAME;
            }); // @ts-ignore - typescript is not smart enough to know `siteFilter` will only ever be the sites filter
            sites = (siteFilter === null || siteFilter === void 0 ? void 0 : siteFilter.values) || defaultSites || []; // Remove sites from the filters array
            filters = allFilters.filter(function (filter) {
              return filter['@type'] !== SITE_FILTER_TYPE_NAME;
            }).map(function (f) {
              return f;
            });
            permittedSections = sections.filter(function (section) {
              return allPermissionedScopes.indexOf(section.id) >= 0;
            }).filter(function (section) {
              return section.isEnabled ? section.isEnabled(query, filters) : true;
            });
            scopes = permittedSections.map(function (section) {
              return section.scope;
            });
            _context.next = 9;
            return aggregatorClient.search({
              query: query,
              scopes: scopes,
              modelParams: [],
              experience: experience,
              context: context,
              filters: filters,
              sites: sites
            });
          case 9:
            _yield$aggregatorClie = _context.sent;
            response = _yield$aggregatorClie.response;
            sectionsWithResults = permittedSections.map(function (_ref3) {
              var scope = _ref3.scope,
                id = _ref3.id,
                title = _ref3.title,
                resultMapper = _ref3.resultMapper,
                rest = _objectWithoutProperties(_ref3, _excluded);
              var results = response.retrieveScope(scope);
              if (results === null) {
                throw new Error("Scope ".concat(scope, " was not found in the response, despite being requested"));
              }
              return _objectSpread(_objectSpread({}, rest), {}, {
                id: id,
                title: title,
                size: results.size,
                searchResults: resultMapper(results) || [],
                resultMapper: resultMapper
              });
            });
            scopesWithSize = response.rawData.scopes.filter(function (_ref4) {
              var size = _ref4.size;
              return size;
            });
            reponseHasSize = scopesWithSize.length > 0;
            totalSizeAsReportedByAPI = reponseHasSize ? response.rawData.scopes.reduce(function (count, scope) {
              return count + (scope.size || 0);
            }, 0) : undefined;
            return _context.abrupt("return", {
              size: totalSizeAsReportedByAPI,
              sections: sectionsWithResults
            });
          case 16:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }();
};

/**
 * Returns a {@link ResultSuppliers} object containing suppliers for pre and post query which
 * are backed by the search platform.
 *
 * The suppliers consider registered scopes, that is scopes that have had permissions evaluated for. I.e,
 * these suppliers will not request scopes that the {@link SearchDialogProduct} permission supplier has
 * returned no permission for.
 *
 * @param id The product id to generate suppliers for
 * @param sections the sections in the search dialog to generate suppliers for. These sections are returned in each supplier,
 * with results from the search platform attached to them.
 * @param overrides to override the item suppliers with input props
 * @returns a {@link ResultSuppliers} object containing a pre and post query supplier
 */
export var useDefaultSuppliers = function useDefaultSuppliers(id, sections, overrides) {
  var _overrides$preQueryIt, _overrides$postQueryI;
  var aggregatorClient = useTypedAggregatorClient();
  var sessionId = useSearchSessionId();
  var search = createSearchSupplier(aggregatorClient, sections, {
    sessionId: sessionId,
    referrerId: ''
  }, PLACEHOLDER_EXPERIENCE);
  return {
    preQueryItemSupplier: (_overrides$preQueryIt = overrides === null || overrides === void 0 ? void 0 : overrides.preQueryItemSupplier) !== null && _overrides$preQueryIt !== void 0 ? _overrides$preQueryIt : function (args) {
      return search(_objectSpread(_objectSpread({}, args), {}, {
        query: '',
        filters: []
      }));
    },
    postQueryItemSupplier: (_overrides$postQueryI = overrides === null || overrides === void 0 ? void 0 : overrides.postQueryItemSupplier) !== null && _overrides$postQueryI !== void 0 ? _overrides$postQueryI : search
  };
};